import { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { HolidayWarning } from '../components/agenda/useHolidays';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useStatuses } from '../hooks/useStatuses';
import { CalendarBlank, Plus, CaretLeft, CaretRight, Trash, ClipboardText, User, Heartbeat, UserPlus, Lock, UserCircle, ClockCounterClockwise, Phone } from '@phosphor-icons/react';
import VolunteerModal from './VolunteerModal';
import { MiniDayView } from '../components/agenda/MiniDayView';
import { PhoneCallsSheet } from '../components/agenda/PhoneCallsSheet';
import VolunteerComboSearch from '../components/agenda/VolunteerComboSearch';
import ScheduleBlockDialog from '../components/agenda/ScheduleBlockDialog';
import UserScheduleBlocksSection from '../components/UserScheduleBlocksSection';
import AgendaContextMenu from '../components/agenda/AgendaContextMenu';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import * as SelectPrimitive from '@radix-ui/react-select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { errText } from '../utils/errors';
import { VIEWS, fmtDate, pad, addDays, startOfWeek, getDaysInMonth, MONTHS_ES, MONTHS_EN } from '../components/agenda/agendaConstants';
import { DayView } from '../components/agenda/DayView';
import { WeekView } from '../components/agenda/WeekView';
import { MonthView } from '../components/agenda/MonthView';
import ScheduleYearView from '../components/agenda/ScheduleYearView';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import AppointmentAuditTimeline from '../components/agenda/AppointmentAuditTimeline';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function StudyHoverInfo({ study, anchorRect }) {
  const popRef = useRef(null);
  const [style, setStyle] = useState({ opacity: 0, position: 'fixed', pointerEvents: 'none', zIndex: 9999 });

  useEffect(() => {
    if (!popRef.current || !anchorRect) return;
    const pop = popRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top = anchorRect.bottom + 6;
    if (top + pop.height > vh - 8) top = anchorRect.top - pop.height - 6;
    let left = anchorRect.left;
    if (left + pop.width > vw - 12) left = vw - pop.width - 12;
    if (left < 8) left = 8;
    if (top < 8) top = 8;
    setStyle({ position: 'fixed', top, left, zIndex: 9999, pointerEvents: 'none', opacity: 1, transition: 'opacity 120ms ease-in' });
  }, [anchorRect]);

  return createPortal(
    <div ref={popRef} data-testid={`study-hover-${study.study_id}`} className="w-64 bg-white border border-stone-200 rounded-md shadow-xl overflow-hidden" style={style}>
      <div className="h-1.5" style={{ backgroundColor: study.color || '#6B7280' }} />
      <div className="p-3 space-y-2">
        <div className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: study.color || '#6B7280' }} />
          <span className="text-xs font-bold text-stone-800 font-mono">{study.code}</span>
        </div>
        <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1.5 text-xs">
          <span className="text-stone-400 font-medium">Estudio</span>
          <span className="text-stone-800 font-semibold truncate">{study.title || '—'}</span>
          <span className="text-stone-400 font-medium">Cliente</span>
          <span className="text-stone-800 truncate">{study.client_name || '—'}</span>
        </div>
      </div>
    </div>,
    document.body
  );
}

function StudyLegendChip({ study }) {
  const [hovered, setHovered] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const timerRef = useRef(null);
  const chipRef = useRef(null);

  const onEnter = useCallback(() => {
    timerRef.current = setTimeout(() => {
      if (chipRef.current) setAnchorRect(chipRef.current.getBoundingClientRect());
      setHovered(true);
    }, 200);
  }, []);

  const onLeave = useCallback(() => {
    clearTimeout(timerRef.current);
    setHovered(false);
    setAnchorRect(null);
  }, []);

  return (
    <div ref={chipRef} className="flex items-center gap-1.5 cursor-default" onMouseEnter={onEnter} onMouseLeave={onLeave} data-testid={`study-legend-${study.study_id}`}>
      <span className="w-3 h-3 rounded-[2px]" style={{ backgroundColor: study.color || '#6B7280' }} />
      <span className="text-xs text-stone-600">{study.code}</span>
      {hovered && anchorRect && <StudyHoverInfo study={study} anchorRect={anchorRect} />}
    </div>
  );
}

export default function Agenda() {
  const { lang } = useLanguage();
  const routerNavigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = (user?.roles || (user?.role ? [user.role] : [])).includes('admin');
  // URS-F486: estados de cita configurables por admin.
  const { statuses: apptStatuses, defaultKey: apptDefaultStatus } = useStatuses('appointment');
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setView] = useState(VIEWS.day);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showCalls, setShowCalls] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [studies, setStudies] = useState([]);
  const [studyVolunteers, setStudyVolunteers] = useState({});
  const [scheduleSlots, setScheduleSlots] = useState([]);
  const [scheduleBlocks, setScheduleBlocks] = useState([]);
  // URS-F396: filtro de estudios pasa a MULTISELECCION.
  //   null            -> "Todos" (equivalente al antiguo 'all')
  //   []              -> Ninguno (no muestra nada)
  //   [id1, id2, ...] -> solo esos estudios
  const [filterStudies, setFilterStudies] = useState(null);
  // Subtype filter - null = all selected (default). Array of strings = only those selected.
  const [filterSubtypes, setFilterSubtypes] = useState(null);
  // URS-F247: status filter ('all' = sin filtro, o uno de los apptStatusColors keys).
  const [filterStatus, setFilterStatus] = useState('all');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showApptAudit, setShowApptAudit] = useState(false);
  const [form, setForm] = useState({ study_id: '', volunteer_id: '', volunteer_name: '', visit_code: '', date: '', time: '', status: apptDefaultStatus || 'citada', notes: '' });
  const [studyVols, setStudyVols] = useState([]);
  const [studyVisits, setStudyVisits] = useState([]);
  const [allVolunteers, setAllVolunteers] = useState([]);
  const [isQsalud, setIsQsalud] = useState(false);
  const [saving, setSaving] = useState(false);
  // Qsalud review gate (only for Valoracion visits)
  const [qsaludReviewedAt, setQsaludReviewedAt] = useState(null);
  const [showQsaludReview, setShowQsaludReview] = useState(false);
  // URS-F249: dialog overlay con la ficha COMPLETA del voluntario (mismo
  // patron iframe que showQsaludReview, pero apuntando a la ficha raiz).
  const [showVolunteerFull, setShowVolunteerFull] = useState(false);
  const dragApptRef = useRef(null);
  const returnToRef = useRef(null);
  // URS-F196: anti-concurrencia agenda
  const [slotLocks, setSlotLocks] = useState([]); // locks de otros usuarios visibles en la agenda
  const [myLock, setMyLock] = useState(null); // lock activo de este usuario (slot reservado del modal)
  const [lockedFromSlot, setLockedFromSlot] = useState(false); // true cuando el modal se abrio desde un hueco de programacion: study/visit no editables
  const heartbeatRef = useRef(null);

  // URS-F252: bloqueos globales en Agenda
  const [showBlockDialog, setShowBlockDialog] = useState(false);
  // URS-F416e: acceso rapido a "Mis bloqueos personales" desde la Agenda.
  const [showMyBlocksDialog, setShowMyBlocksDialog] = useState(false);
  const [blockDefaults, setBlockDefaults] = useState({ date: '', startTime: '', endTime: '' });
  const [ctxMenu, setCtxMenu] = useState({ open: false, x: 0, y: 0, date: '', time: '' });
  const [blockToDelete, setBlockToDelete] = useState(null); // {block_id, reason, ...}

  const lb = lang === 'es'
    ? { title: 'Agenda', day: 'Dia', week: 'Semana', month: 'Mes', schedule: 'Programacion', today: 'Hoy', new: 'Nueva Cita', edit: 'Editar Cita', study: 'Estudio', volunteer: 'Voluntario', visit: 'Visita', date: 'Fecha', time: 'Hora', status: 'Estado', notes: 'Notas', save: 'Guardar', cancel: 'Cancelar', delete: 'Eliminar', confirm_delete: 'Eliminar esta cita?', citada: 'Citada', anulada: 'Anulada', no_asiste: 'No asiste', sala_espera: 'Sala de espera', especialista: 'Especialista', sondas: 'Sondas', terminada: 'Terminada', created: 'Cita creada', updated: 'Cita actualizada', deleted: 'Cita eliminada', all_studies: 'Todos los estudios', select_study: 'Seleccionar estudio', select_volunteer: 'Seleccionar voluntario', select_visit: 'Seleccionar visita', moved: 'Cita movida', subtype: 'Subtipo', all_subtypes: 'Todos los subtipos', select_all: 'Todos', select_none: 'Ninguno', subtypes_count: (n, t) => `${n}/${t} subtipos`, studies_count: (n, t) => `${n}/${t} estudios`, no_subtype: '(sin subtipo)' }
    : { title: 'Schedule', day: 'Day', week: 'Week', month: 'Month', schedule: 'Programming', today: 'Today', new: 'New Appointment', edit: 'Edit Appointment', study: 'Study', volunteer: 'Volunteer', visit: 'Visit', date: 'Date', time: 'Time', status: 'Status', notes: 'Notes', save: 'Save', cancel: 'Cancel', delete: 'Delete', confirm_delete: 'Delete this appointment?', citada: 'Scheduled', anulada: 'Cancelled', no_asiste: 'No show', sala_espera: 'Waiting room', especialista: 'Specialist', sondas: 'Probes', terminada: 'Completed', created: 'Appointment created', updated: 'Appointment updated', deleted: 'Appointment deleted', all_studies: 'All studies', select_study: 'Select study', select_volunteer: 'Select volunteer', select_visit: 'Select visit', moved: 'Appointment moved', subtype: 'Subtype', all_subtypes: 'All subtypes', select_all: 'All', select_none: 'None', subtypes_count: (n, t) => `${n}/${t} subtypes`, studies_count: (n, t) => `${n}/${t} studies`, no_subtype: '(no subtype)' };

  const months = lang === 'es' ? MONTHS_ES : MONTHS_EN;

  const dateRange = useMemo(() => {
    if (view === VIEWS.day) {
      return { from: fmtDate(currentDate), to: fmtDate(currentDate) };
    } else if (view === VIEWS.week) {
      const ws = startOfWeek(currentDate);
      return { from: fmtDate(ws), to: fmtDate(addDays(ws, 6)) };
    } else {
      const y = currentDate.getFullYear(), m = currentDate.getMonth();
      return { from: `${y}-${pad(m + 1)}-01`, to: `${y}-${pad(m + 1)}-${pad(getDaysInMonth(y, m))}` };
    }
  }, [view, currentDate]);

  const loadSeq = useRef(0);
  const loadData = () => {
    const seq = ++loadSeq.current;
    // URS-F286: NO filtramos por study_id en el backend para poder seguir
    // contando citas por estudio en el dropdown del filtro. El filtrado por
    // estudio se aplica client-side en visibleAppointments.
    const params = new URLSearchParams({ date_from: dateRange.from, date_to: dateRange.to });
    Promise.all([
      axios.get(`${API_URL}/api/appointments?${params}`, { withCredentials: true }),
      axios.get(`${API_URL}/api/studies`, { withCredentials: true }),
      axios.get(`${API_URL}/api/schedule/all`, { withCredentials: true }),
      axios.get(`${API_URL}/api/schedule/blocks`, { withCredentials: true }),
    ]).then(async ([ar, sr, scr, bks]) => {
      const appts = ar.data;
      setStudies(sr.data);
      setScheduleSlots(scr.data || []);
      setScheduleBlocks(Array.isArray(bks.data) ? bks.data : []);
      const studyIds = [...new Set(appts.map(a => a.study_id).filter(Boolean))];
      const volMap = {};
      await Promise.all(studyIds.map(sid =>
        axios.get(`${API_URL}/api/studies/${sid}/volunteers`, { withCredentials: true })
          .then(r => { (r.data || []).forEach(sv => { volMap[`${sid}_${sv.volunteer_id}`] = sv.volunteer_name || ''; }); })
          .catch(() => {})
      ));
      appts.forEach(a => {
        if (!a.volunteer_name) {
          a.volunteer_name = volMap[`${a.study_id}_${a.volunteer_id}`] || '';
        }
      });
      // Descarta respuestas obsoletas al navegar rápido entre semanas.
      if (seq === loadSeq.current) setAppointments(appts);
    }).catch(() => {});
  };

  useEffect(() => { loadData(); }, [dateRange]);

  // URS-F252: handlers para bloquear huecos globales desde la Agenda.
  const reloadBlocks = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/schedule/blocks`, { withCredentials: true });
      setScheduleBlocks(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  };

  const openBlockDialog = (date, startTime, endTime) => {
    setBlockDefaults({ date: date || fmtDate(currentDate), startTime: startTime || '09:00', endTime: endTime || '09:15' });
    setShowBlockDialog(true);
  };

  const addMinutes = (timeStr, minutes) => {
    const [h, m] = (timeStr || '00:00').split(':').map(Number);
    const total = h * 60 + m + minutes;
    return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  };

  const handleContextMenuSlot = (date, time, x, y) => {
    setCtxMenu({ open: true, x, y, date, time });
  };

  const handleClickBlock = (block) => {
    setBlockToDelete(block);
  };

  const confirmDeleteBlock = async () => {
    if (!blockToDelete) return;
    try {
      await axios.delete(`${API_URL}/api/schedule/blocks/${blockToDelete.block_id}`, { withCredentials: true });
      toast.success(lang === 'es' ? 'Bloqueo eliminado' : 'Block deleted');
      setBlockToDelete(null);
      await reloadBlocks();
    } catch (err) {
      toast.error(errText(err, 'Error'));
    }
  };

  // URS-F196: cargar locks activos del rango visible + refresh 30s
  const loadLocks = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/api/agenda/slot-locks?date_from=${dateRange.from}&date_to=${dateRange.to}`,
        { withCredentials: true },
      );
      setSlotLocks(Array.isArray(data) ? data : []);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    loadLocks();
    const iv = setInterval(loadLocks, 30000);
    return () => clearInterval(iv);
  }, [dateRange]);

  // URS-F196: heartbeat al lock propio mientras el modal esta abierto
  useEffect(() => {
    if (!myLock) return undefined;
    heartbeatRef.current = setInterval(async () => {
      try {
        await axios.post(`${API_URL}/api/agenda/slot-locks/${myLock.lock_id}/heartbeat`, {}, { withCredentials: true });
      } catch { /* ignore */ }
    }, 60000);
    return () => { if (heartbeatRef.current) clearInterval(heartbeatRef.current); };
  }, [myLock]);

  // URS-F196: liberar lock en navegacion / cierre de pestana
  useEffect(() => {
    const beforeUnload = () => {
      if (myLock) {
        navigator.sendBeacon(
          `${API_URL}/api/agenda/slot-locks/${myLock.lock_id}/release-beacon`,
          new Blob([JSON.stringify({})], { type: 'application/json' }),
        );
      }
    };
    window.addEventListener('beforeunload', beforeUnload);
    return () => window.removeEventListener('beforeunload', beforeUnload);
  }, [myLock]);

  const releaseMyLock = async () => {
    if (!myLock) return;
    try {
      await axios.delete(`${API_URL}/api/agenda/slot-locks/${myLock.lock_id}`, { withCredentials: true });
    } catch { /* ignore */ }
    setMyLock(null);
    setLockedFromSlot(false);
    loadLocks();
  };

  useEffect(() => {
    // URS-F361: editar cita desde otras vistas pasandole ?edit=<appointment_id>.
    const editId = searchParams.get('edit');
    if (editId) {
      // URS: recordar origen (p.ej. ficha de voluntario) para volver tras borrar.
      returnToRef.current = searchParams.get('from') || null;
      axios.get(`${API_URL}/api/appointments/${editId}`, { withCredentials: true })
        .then(r => { if (r.data) openEdit(r.data); })
        .catch(() => toast.error(lang === 'es' ? 'No se pudo cargar la cita' : 'Could not load appointment'))
        .finally(() => setSearchParams({}, { replace: true }));
      return;
    }
    if (searchParams.get('new') === '1') {
      const preStudy = searchParams.get('study');
      const preVol = searchParams.get('volunteer');
      const preVisit = searchParams.get('visit') || '';
      if (preStudy === 'none' || preVisit === 'Qsalud' || preVisit === 'QSALUD TFNO') {
        // Qsalud mode: no study
        setEditing(null);
        setIsQsalud(true);
        setForm({ study_id: '', volunteer_id: preVol || '', volunteer_name: '', visit_code: preVisit === 'QSALUD TFNO' ? 'QSALUD TFNO' : 'Qsalud', date: fmtDate(currentDate), time: '08:00', status: apptDefaultStatus || 'citada', notes: '' });
        setStudyVols([]);
        setStudyVisits([]);
        // Load all volunteers for global selector
        axios.get(`${API_URL}/api/volunteers?all=true`, { withCredentials: true }).then(r => {
          setAllVolunteers(r.data || []);
          if (preVol) {
            const vol = (r.data || []).find(v => v.volunteer_id === preVol);
            if (vol) {
              const pd = vol.personal_data || {};
              const vname = `${pd.nombre || vol.name || ''} ${pd.apellidos || vol.surname || ''}`.trim();
              setForm(p => ({ ...p, volunteer_name: vname }));
            }
          }
        }).catch(() => {});
        setShowForm(true);
      } else if (preStudy) {
        setEditing(null);
        setIsQsalud(false);
        setForm({ study_id: preStudy, volunteer_id: preVol || '', volunteer_name: '', visit_code: preVisit, date: fmtDate(currentDate), time: '08:00', status: apptDefaultStatus || 'citada', notes: '' });
        loadStudyData(preStudy).then(() => {
          if (preVol) {
            axios.get(`${API_URL}/api/studies/${preStudy}/volunteers`, { withCredentials: true }).then(r => {
              const vol = (r.data || []).find(v => v.volunteer_id === preVol);
              if (vol) setForm(p => ({ ...p, volunteer_name: vol.volunteer_name || '' }));
            }).catch(() => {});
          }
        });
        setShowForm(true);
      } else {
        openNew();
      }
      setSearchParams({}, { replace: true });
    }
  }, []);

  const navigate = (dir) => {
    const d = new Date(currentDate);
    if (view === VIEWS.day) d.setDate(d.getDate() + dir);
    else if (view === VIEWS.week) d.setDate(d.getDate() + dir * 7);
    else d.setMonth(d.getMonth() + dir);
    setCurrentDate(d);
    // URS-F286: si estamos en Programacion, prev/next debe sacarnos a vista dia.
    if (view === VIEWS.schedule) setView(VIEWS.day);
  };

  // URS-F286: "Hoy" siempre vuelve a vista dia (incluso desde Programacion).
  const goToday = () => {
    setCurrentDate(new Date());
    if (view !== VIEWS.day && view !== VIEWS.week && view !== VIEWS.month) setView(VIEWS.day);
    else if (view === VIEWS.schedule) setView(VIEWS.day);
  };

  // URS-F254: cuando la cita se crea para HOY, sugerir la hora actual
  // (redondeada a multiplos de 5 minutos hacia arriba) en lugar del 08:00
  // por defecto, para evitar tener que mover la cita siempre hacia adelante.
  const suggestedTimeForDate = (dateStr) => {
    const today = fmtDate(new Date());
    if (dateStr !== today) return '08:00';
    const now = new Date();
    let mins = now.getHours() * 60 + now.getMinutes();
    mins = Math.ceil(mins / 5) * 5; // round up to next 5-min slot
    // Clamp inside agenda window 08:00 - 20:55
    if (mins < 8 * 60) mins = 8 * 60;
    if (mins > 20 * 60 + 55) mins = 20 * 60 + 55;
    return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
  };

  const openNew = (date, time) => {
    setEditing(null);
    setIsQsalud(false);
    const finalDate = date || fmtDate(currentDate);
    const finalTime = time || suggestedTimeForDate(finalDate);
    setForm({ study_id: '', volunteer_id: '', volunteer_name: '', visit_code: '', date: finalDate, time: finalTime, status: apptDefaultStatus || 'citada', notes: '' });
    setStudyVols([]);
    setStudyVisits([]);
    setAllVolunteers([]);
    setMyLock(null);
    setLockedFromSlot(false);
    setShowForm(true);
  };

  // URS-F196: abrir modal pre-cargado desde un hueco de programacion del calendario.
  // Crea lock anti-concurrencia; si esta ya bloqueado por otro usuario muestra toast 409
  // y NO abre el modal. study/visit se renderizan como readonly (URS-F197).
  const openNewFromSlot = async (slot) => {
    try {
      const { data: lock } = await axios.post(
        `${API_URL}/api/agenda/slot-locks`,
        {
          study_id: slot.study_id,
          visit_code: slot.visit_code,
          date: slot.date,
          start_time: slot.start_time,
        },
        { withCredentials: true },
      );
      setEditing(null);
      setIsQsalud(false);
      setForm({
        study_id: slot.study_id,
        volunteer_id: '',
        volunteer_name: '',
        visit_code: slot.visit_code,
        date: slot.date,
        time: slot.start_time,
        status: apptDefaultStatus || 'citada',
        notes: '',
      });
      setStudyVols([]);
      setStudyVisits([]);
      setAllVolunteers([]);
      setMyLock(lock);
      setLockedFromSlot(true);
      await loadStudyData(slot.study_id);
      setShowForm(true);
      loadLocks();
    } catch (err) {
      if (err?.response?.status === 409) {
        const who = err.response.data?.detail?.locked_by_name || 'otro usuario';
        toast.error(lang === 'es' ? `Hueco bloqueado por ${who}` : `Slot locked by ${who}`);
        loadLocks();
      } else {
        toast.error('Error');
      }
    }
  };

  const openEdit = (appt) => {
    setEditing(appt);
    const qsalud = !appt.study_id || appt.visit_code === 'Qsalud';
    setIsQsalud(qsalud);
    setForm({ study_id: appt.study_id || '', volunteer_id: appt.volunteer_id, volunteer_name: appt.volunteer_name || '', visit_code: appt.visit_code || '', date: appt.date, time: appt.time || '', status: appt.status, notes: appt.notes || '' });
    if (qsalud) {
      setStudyVols([]);
      setStudyVisits([]);
    } else {
      loadStudyData(appt.study_id);
    }
    setShowForm(true);
  };

  const loadStudyData = async (studyId) => {
    if (!studyId) { setStudyVols([]); setStudyVisits([]); return; }
    try {
      const [vr, visr] = await Promise.all([
        axios.get(`${API_URL}/api/studies/${studyId}/volunteers`, { withCredentials: true }),
        axios.get(`${API_URL}/api/studies/${studyId}/visits`, { withCredentials: true }),
      ]);
      setStudyVols(vr.data);
      setStudyVisits(visr.data);
    } catch { setStudyVols([]); setStudyVisits([]); }
  };

  const handleStudyChange = (studyId) => {
    if (studyId === '_qsalud' || studyId === '_qsalud_tfno') {
      setIsQsalud(true);
      setForm(p => ({ ...p, study_id: '', volunteer_id: '', visit_code: studyId === '_qsalud_tfno' ? 'QSALUD TFNO' : 'Qsalud' }));
      setStudyVols([]);
      setStudyVisits([]);
      // Load all volunteers (flat list for dropdown). all=true returns array directly.
      axios.get(`${API_URL}/api/volunteers?all=true`, { withCredentials: true }).then(r => {
        const list = Array.isArray(r.data) ? r.data : (r.data?.data || []);
        setAllVolunteers(list);
      }).catch(() => setAllVolunteers([]));
    } else {
      setIsQsalud(false);
      setForm(p => ({ ...p, study_id: studyId, volunteer_id: '', visit_code: '' }));
      setAllVolunteers([]);
      loadStudyData(studyId);
    }
  };

  const [showOutOfSchedule, setShowOutOfSchedule] = useState(false);
  // URS-F416b (extension): coverage warning tambien desde Agenda.
  const [coverageWarning, setCoverageWarning] = useState(null);
  const [coverageAck, setCoverageAck] = useState(false);
  const [pendingDrop, setPendingDrop] = useState(null);
  const [viewVolunteerId, setViewVolunteerId] = useState(null);

  const isInSchedule = (studyId, date, time) => {
    if (!studyId || !date || !time) return true;
    const studySlots = scheduleSlots.filter(s => s.study_id === studyId);
    if (studySlots.length === 0) return true;
    return studySlots.some(s => s.date === date && s.start_time <= time && s.end_time > time);
  };

  // ---- Qsalud review gate (only for Valoracion / Screening visits) ----
  const stripAccents = (s) => (s || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  const isValVisit = (() => {
    const code = (form.visit_code || '').toUpperCase().trim();
    if (!code) return false;
    if (/^(VAL|VALORACION|VALORACI[OÓ]N|SCREENING|SCR|S0|V0)$/i.test(code)) return true;
    // URS-F463 fix: NO tratar "basal" como Valoracion. La basal es la primera
    // visita del protocolo y saca voluntarios de "Aptos", no de "Pendientes".
    const v = studyVisits.find(x => (x.code || '').toUpperCase() === code);
    if (v) {
      if (v.visit_type === 'valoracion') return true;
      const n = stripAccents(v.name);
      if (n.startsWith('valoracion') || n.startsWith('screening') || n === 'cribado') return true;
    }
    return false;
  })();
  const qsaludRecent = (() => {
    if (!qsaludReviewedAt) return false;
    const ts = new Date(qsaludReviewedAt).getTime();
    if (Number.isNaN(ts)) return false;
    return (Date.now() - ts) < 24 * 60 * 60 * 1000;
  })();
  const qsaludGateOpen = !isValVisit || qsaludRecent;

  // Fetch qsalud_last_reviewed_at when volunteer changes / dialog opens
  useEffect(() => {
    if (!form.volunteer_id || !showForm) {
      setQsaludReviewedAt(null);
      return;
    }
    let cancelled = false;
    axios.get(`${API_URL}/api/volunteers/${form.volunteer_id}`, { withCredentials: true })
      .then(r => { if (!cancelled) setQsaludReviewedAt(r.data?.qsalud_last_reviewed_at || null); })
      .catch(() => { if (!cancelled) setQsaludReviewedAt(null); });
    return () => { cancelled = true; };
  }, [form.volunteer_id, showForm]);

  // Listen for postMessage from the embedded VolunteerForm (Qsalud popup,
  // appointment status changes from the volunteer record iframe, etc.)
  useEffect(() => {
    const handler = (ev) => {
      if (!ev?.data) return;
      if (ev.data.type === 'qsalud_saved') {
        if (form.volunteer_id && ev.data.volunteer_id === form.volunteer_id) {
          setQsaludReviewedAt(ev.data.at || new Date().toISOString());
          toast.success(lang === 'es' ? 'Qsalud guardado y revisado' : 'Qsalud saved and reviewed');
          setShowQsaludReview(false);
        }
        return;
      }
      // URS-F307: el iframe de la ficha del voluntario notifica cuando se
      // cambia el estado de una cita; refrescamos la agenda al instante para
      // que el operador vea el nuevo estado sin recargar la pagina.
      if (ev.data.type === 'appointment_status_changed') {
        // Optimistic update del array local para no esperar al loadData.
        setAppointments(list => list.map(a =>
          a.appointment_id === ev.data.appointment_id
            ? { ...a, status: ev.data.status }
            : a
        ));
        // Recarga completa para mantener consistencia con el backend (filtros,
        // visibilidad de anuladas, recuento por estudio, etc.)
        loadData();
        return;
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.volunteer_id, lang]);

  const goToFillQuestionnaire = () => {
    closeForm();
    if (isValVisit) {
      routerNavigate(`/studies/${form.study_id}?tab=volunteers&volunteers_tab=pendientes&highlight_volunteer=${form.volunteer_id}`);
    } else {
      routerNavigate(`/studies/${form.study_id}?tab=volunteers&volunteer_id=${form.volunteer_id}`);
    }
  };

  // URS-F196: cierre del modal libera el lock anti-concurrencia
  const closeForm = () => {
    setShowForm(false);
    if (myLock) {
      releaseMyLock();
    }
  };

  // ----------------------------------------------------------------
  // Volunteer list filtered by selected visit (Estudio → Visita → Voluntario)
  // ----------------------------------------------------------------
  // Detect T0 visits the same way we detect VAL: by code or visit name.
  const isT0Visit = (() => {
    const code = (form.visit_code || '').toUpperCase().trim();
    if (!code) return false;
    if (/^T0$/i.test(code) || /^TIEMPO\s*0$/i.test(code) || /^BASELINE$/i.test(code)) return true;
    const v = studyVisits.find(x => (x.code || '').toUpperCase() === code);
    if (v) {
      const n = stripAccents(v.name);
      if (n === 'tiempo 0' || n.startsWith('basal') || n.startsWith('baseline')) return true;
    }
    // URS-F463: la "basal" es dinamicamente la primera visita del protocolo
    // (menor `day`, excluyendo VAL y Eventos Adversos). Esa visita saca
    // voluntarios de "Aptos" aunque su codigo no sea T0 (p.ej. T-14D).
    const protocol = (studyVisits || []).filter(x => {
      const c = (x.code || '').toUpperCase();
      return c && c !== 'VAL' && c !== 'EA' && x.visit_type !== 'valoracion' && x.visit_type !== 'eventos_adversos';
    });
    if (protocol.length === 0) return false;
    const sorted = [...protocol].sort((a, b) => (a.day ?? 0) - (b.day ?? 0));
    const firstCode = (sorted[0]?.code || '').toUpperCase();
    return !!firstCode && firstCode === code;
  })();

  // Pick the right pool of volunteers for the appointment dialog
  const volunteersForAppointment = useMemo(() => {
    if (isQsalud) {
      return (allVolunteers || []).map(v => {
        const pd = v.personal_data || {};
        const name = `${pd.nombre || v.name || ''} ${pd.apellidos || v.surname || ''}`.trim() || v.volunteer_id;
        return {
          ...v,
          volunteer_name: name,
          volunteer_dni: pd.dni || '',
          study_volunteer_code: pd.num_voluntario || '',
        };
      });
    }
    if (!form.study_id || !form.visit_code) return [];
    let allowedStatuses;
    if (isValVisit) allowedStatuses = ['pendiente'];
    else if (isT0Visit) allowedStatuses = ['apto'];
    else allowedStatuses = ['incluido'];
    let pool = (studyVols || []).filter(sv => allowedStatuses.includes(sv.status));
    // Editing: keep current volunteer in the list even if their status no
    // longer matches (so the dialog doesn't appear to lose them).
    if (editing && form.volunteer_id && !pool.some(p => p.volunteer_id === form.volunteer_id)) {
      const cur = (studyVols || []).find(sv => sv.volunteer_id === form.volunteer_id);
      if (cur) pool = [cur, ...pool];
    }
    return pool;
  }, [isQsalud, allVolunteers, studyVols, form.study_id, form.visit_code, form.volunteer_id, isValVisit, isT0Visit, editing]);

  const volunteerEmptyHint = (() => {
    if (isQsalud || !form.study_id || !form.visit_code) return lang === 'es' ? 'Sin coincidencias.' : 'No matches.';
    if (isValVisit) return lang === 'es' ? 'No hay voluntarios pendientes en este estudio.' : 'No pending volunteers in this study.';
    if (isT0Visit) return lang === 'es' ? 'No hay voluntarios aptos en este estudio.' : 'No fit volunteers in this study.';
    return lang === 'es' ? 'No hay voluntarios incluidos en este estudio.' : 'No included volunteers in this study.';
  })();

  const volunteerListEmpty = !isQsalud && form.study_id && form.visit_code && volunteersForAppointment.length === 0;

  const formatVolForCombo = (o) => {
    if (isQsalud) {
      return `${o.study_volunteer_code ? `${o.study_volunteer_code} — ` : ''}${o.volunteer_name || o.volunteer_id}`;
    }
    return `${o.study_volunteer_code || ''} — ${o.volunteer_name || o.volunteer_id}`.replace(/^—\s*/, '');
  };

  const handleSave = async () => {
    if (isQsalud) {
      if (!form.volunteer_id || !form.date) {
        toast.error(lang === 'es' ? 'Voluntario y fecha son obligatorios' : 'Volunteer and date are required');
        return;
      }
    } else {
      if (!form.study_id || !form.volunteer_id || !form.date) {
        toast.error(lang === 'es' ? 'Estudio, voluntario y fecha son obligatorios' : 'Study, volunteer and date are required');
        return;
      }
      const blk = scheduleBlocks.find(b => b.study_id === form.study_id && b.date === form.date && b.start_time <= form.time && b.end_time > form.time);
      if (blk) {
        toast.error(lang === 'es'
          ? `Hueco bloqueado: ${blk.reason || ''} (${blk.start_time}-${blk.end_time})`
          : `Slot blocked: ${blk.reason || ''} (${blk.start_time}-${blk.end_time})`);
        return;
      }
      if (!isInSchedule(form.study_id, form.date, form.time)) {
        setShowOutOfSchedule(true);
        return;
      }
    }
    await doSave();
  };

  // URS-F416: helpers de cobertura de staff — mismo comportamiento que en
  // AppointmentFormDialog, para que crear cita desde la Agenda tambien
  // respete los bloqueos personales de IP/CoIP.
  const _endOfSlot = (start) => {
    if (!start || !/^\d{2}:\d{2}$/.test(start)) return start || '08:30';
    const [h, m] = start.split(':').map(Number);
    let total = h * 60 + m + 30;
    if (total >= 24 * 60) total = 24 * 60 - 1;
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  };

  const checkCoverage = async (t = null) => {
    if (!t && isQsalud) return null;
    const { study_id, date, time } = t || form;
    if (!study_id || !date || !time) return null;
    try {
      const resp = await axios.post(
        `${API_URL}/api/user-schedule-blocks/coverage`,
        { study_id, date, start_time: time, end_time: _endOfSlot(time) },
        { withCredentials: true },
      );
      return resp.data || null;
    } catch {
      return null;
    }
  };

  const doSave = async (opts = {}) => {
    // URS-F416: gate de cobertura solo para citas NUEVAS (edicion no cambia
    // asignacion) y saltable con opts.skipCoverage.
    // Al editar solo si cambia fecha u hora.
    const movedEdit = editing && (editing.date !== form.date || editing.time !== form.time);
    if ((!editing || movedEdit) && !opts.skipCoverage) {
      const cov = await checkCoverage();
      if (cov && (cov.coverage === 'partial' || cov.coverage === 'none') && (cov.staff?.length || 0) > 0) {
        setCoverageWarning(cov);
        setCoverageAck(false);
        return;
      }
    }
    setShowOutOfSchedule(false);
    setSaving(true);
    // URS-F341 v3: abrir popup ANTES del await sincronizado con el click.
    const printWin = opts.printAfter ? window.open('', '_blank') : null;
    if (opts.printAfter && !printWin) {
      toast.error(lang === 'es' ? 'Habilita las ventanas emergentes para imprimir.' : 'Allow popups to print.');
      setSaving(false);
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/appointments/${editing.appointment_id}`, { date: form.date, time: form.time, status: form.status, notes: form.notes, visit_code: form.visit_code }, { withCredentials: true });
        toast.success(lb.updated);
      } else {
        const payload = isQsalud
          ? { study_id: 'none', volunteer_id: form.volunteer_id, volunteer_name: form.volunteer_name, visit_code: form.visit_code === 'QSALUD TFNO' ? 'QSALUD TFNO' : 'Qsalud', date: form.date, time: form.time, status: form.status, notes: form.notes }
          : form;
        await axios.post(`${API_URL}/api/appointments`, payload, { withCredentials: true });
        toast.success(lb.created);
      }
      if (printWin) {
        printAppointmentTicket(printWin);
      }
      setShowForm(false);
      if (myLock) releaseMyLock();
      // URS: si la cita se abrio desde la ficha de un voluntario, volver alli
      // (subpestana Citas) tras guardar, igual que al borrar.
      const rt = returnToRef.current;
      if (editing && rt && rt.startsWith('volunteer:')) {
        const volId = rt.slice('volunteer:'.length);
        returnToRef.current = null;
        if (volId) {
          setSaving(false);
          routerNavigate(`/volunteers/${volId}?tab=appointments`);
          return;
        }
      }
      loadData();
    } catch (err) {
      if (printWin && !printWin.closed) { try { printWin.close(); } catch { /* noop */ } }
      toast.error(errText(err));
    }
    setSaving(false);
  };

  // URS-F341 v3: ticket Goya 80mm. Recibe la ventana ya abierta para evitar
  // el bloqueador de popups.
  const printAppointmentTicket = (printWin) => {
    const w = printWin || window.open('', '_blank');
    if (!w) {
      toast.error(lang === 'es' ? 'Habilita las ventanas emergentes para imprimir.' : 'Allow popups to print.');
      return;
    }
    const study = studies.find(s => s.study_id === form.study_id);
    // URS-F491 (privacidad/ciego): NO imprimir nombre del voluntario ni
    // nombre/codigo del estudio. Sustituimos el nombre del voluntario por
    // su codigo. El SUBTIPO (phase, ej. Dental) SI se imprime; si el
    // estudio no tiene subtipo (ej. Medicamento) queda en blanco (NUNCA
    // hacemos fallback al codigo del estudio).
    const studyPhase = study?.phase || '';
    const selectedVol = volunteersForAppointment.find(v => v.volunteer_id === form.volunteer_id);
    const volCode = selectedVol?.study_volunteer_code || form.volunteer_id || '—';
    const row = `<tr>
        <td class="c-date">${form.date || '-'}</td>
        <td class="c-time">${form.time || '-'}</td>
        <td class="c-visit">${form.visit_code || ''}<span class="study-sub">${studyPhase || ''}</span></td>
      </tr>`;
    const logoUrl = window.location.origin + '/logo-goya.png';
    const html = `<!DOCTYPE html><html><head><title>Cita ${form.visit_code || ''}</title><style>
      @page { size: 80mm auto; margin: 0 1mm 1mm 1mm; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'Helvetica Neue', Arial, sans-serif; width: 76mm; padding: 0 1mm 1.5mm 1mm; color: #000; font-size: 12px; line-height: 1.3; }
      .ticket { page-break-after: auto; }
      .header { text-align: center; border-bottom: 2px solid #000; padding-bottom: 1.5mm; margin-bottom: 1.5mm; }
      .header img { width: 55mm; height: auto; margin-bottom: 0.5mm; filter: grayscale(100%) contrast(160%); }
      .header .phone { font-size: 11px; margin-top: 0.8mm; font-weight: 600; }
      .header .ev-clinicas { font-size: 10px; margin-top: 0.6mm; letter-spacing: 0.3px; }
      .header .name { font-size: 12px; font-weight: 700; margin-top: 1.5mm; text-transform: uppercase; line-height: 1.15; }
      .header .subtitle { font-size: 11px; margin-top: 1mm; font-weight: 600; letter-spacing: 1px; }
      table { width: 100%; border-collapse: collapse; margin-bottom: 1.5mm; }
      th { font-size: 10px; text-align: left; border-bottom: 2px solid #000; padding: 1mm 1mm; font-weight: 700; text-transform: uppercase; letter-spacing: 0.3px; }
      td { font-size: 13px; padding: 2mm 1mm; border-bottom: 1px dashed #999; font-weight: 600; vertical-align: top; }
      td.c-date { white-space: nowrap; }
      td.c-time { font-weight: 700; }
      td.c-visit { font-weight: 700; }
      td.c-visit .study-sub { display: block; font-size: 9px; font-weight: 500; color: #000; margin-top: 0.4mm; text-transform: uppercase; letter-spacing: 0.3px; }
    </style></head><body><div class="ticket">
      <div class="header">
        <img src="${logoUrl}" alt="Goya Innova" />
        <div class="phone">Tel: 911 928 904</div>
        <div class="ev-clinicas">Evaluaciones Cl\u00ednicas</div>
        <div class="name">${volCode}</div>
        <div class="subtitle">CITA</div>
      </div>
      <table><thead><tr><th>Fecha</th><th>Hora</th><th>Visita</th></tr></thead><tbody>${row}</tbody></table>
      </div>
      <script>window.onload=function(){window.print();}<\/script>
    </body></html>`;
    w.document.open();
    w.document.writeln(html);
    w.document.close();
  };

  const handleSaveAndPrint = async () => {
    if (isQsalud) {
      if (!form.volunteer_id || !form.date) {
        toast.error(lang === 'es' ? 'Voluntario y fecha son obligatorios' : 'Volunteer and date are required');
        return;
      }
    } else {
      if (!form.study_id || !form.volunteer_id || !form.date) {
        toast.error(lang === 'es' ? 'Estudio, voluntario y fecha son obligatorios' : 'Study, volunteer and date are required');
        return;
      }
      const blk = scheduleBlocks.find(b => b.study_id === form.study_id && b.date === form.date && b.start_time <= form.time && b.end_time > form.time);
      if (blk) {
        toast.error(lang === 'es'
          ? `Hueco bloqueado: ${blk.reason || ''} (${blk.start_time}-${blk.end_time})`
          : `Slot blocked: ${blk.reason || ''} (${blk.start_time}-${blk.end_time})`);
        return;
      }
      if (!isInSchedule(form.study_id, form.date, form.time)) {
        setShowOutOfSchedule(true);
        return;
      }
    }
    await doSave({ printAfter: true });
  };

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingAppt, setDeletingAppt] = useState(false);

  const handleDelete = async () => {
    if (!editing) return;
    setDeletingAppt(true);
    try {
      await axios.delete(`${API_URL}/api/appointments/${editing.appointment_id}`, { withCredentials: true });
      toast.success(lb.deleted);
      setShowForm(false);
      setShowDeleteConfirm(false);
      // URS: si la cita se abrio desde la ficha de un voluntario, volver alli
      // (subpestana Citas) en lugar de quedarnos en la Agenda.
      const rt = returnToRef.current;
      if (rt && rt.startsWith('volunteer:')) {
        const volId = rt.slice('volunteer:'.length);
        returnToRef.current = null;
        if (volId) {
          routerNavigate(`/volunteers/${volId}?tab=appointments`);
          return;
        }
      }
      loadData();
    } catch (err) { toast.error(errText(err)); }
    setDeletingAppt(false);
  };

  const handleDragStart = (e, appt) => {
    dragApptRef.current = appt;
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = async (newDate, newTime) => {
    const appt = dragApptRef.current;
    dragApptRef.current = null;
    if (!appt) return;
    if (appt.date === newDate && appt.time === newTime) return;
    if (appt.study_id) {
      const blk = scheduleBlocks.find(b => b.study_id === appt.study_id && b.date === newDate && b.start_time <= newTime && b.end_time > newTime);
      if (blk) {
        toast.error(lang === 'es'
          ? `Hueco bloqueado: ${blk.reason || ''} (${blk.start_time}-${blk.end_time})`
          : `Slot blocked: ${blk.reason || ''} (${blk.start_time}-${blk.end_time})`);
        return;
      }
    }
    if (!isInSchedule(appt.study_id, newDate, newTime)) {
      setPendingDrop({ appt, newDate, newTime });
      setShowOutOfSchedule(true);
      return;
    }
    await moveWithCoverage(appt, newDate, newTime);
  };

  const [coverageMove, setCoverageMove] = useState(null);
  // Arrastrar: comprueba la cobertura del nuevo hueco antes de mover.
  const moveWithCoverage = async (appt, newDate, newTime) => {
    if (appt.study_id) {
      const cov = await checkCoverage({ study_id: appt.study_id, date: newDate, time: newTime });
      if (cov && (cov.coverage === 'partial' || cov.coverage === 'none') && (cov.staff?.length || 0) > 0) {
        setCoverageMove({ appt, newDate, newTime });
        setCoverageWarning(cov);
        setCoverageAck(false);
        return;
      }
    }
    await executeDrop(appt, newDate, newTime);
  };

  const executeDrop = async (appt, newDate, newTime) => {
    try {
      await axios.put(`${API_URL}/api/appointments/${appt.appointment_id}`, {
        date: newDate, time: newTime, status: appt.status, notes: appt.notes || '', visit_code: appt.visit_code || '',
      }, { withCredentials: true });
      toast.success(lb.moved);
      loadData();
    } catch (err) { toast.error(errText(err)); }
  };

  const headerTitle = () => {
    if (view === VIEWS.month) return `${months[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    if (view === VIEWS.day) return `${currentDate.getDate()} ${months[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    const ws = startOfWeek(currentDate);
    const we = addDays(ws, 6);
    if (ws.getMonth() === we.getMonth()) return `${ws.getDate()} - ${we.getDate()} ${months[ws.getMonth()]} ${ws.getFullYear()}`;
    return `${ws.getDate()} ${months[ws.getMonth()]} - ${we.getDate()} ${months[we.getMonth()]} ${we.getFullYear()}`;
  };

  const activeStudies = studies.filter(s => s.status === 'activo');
  const activeStudiesWithVisits = activeStudies.filter(s => (s.visit_count || 0) > 0);

  // --- Subtype (phase) multi-select filter --------------------------------
  // The subtype is stored in study.phase (e.g. 'Capilar', 'Ginecologia', 'Fase I'...).
  // Compute the unique subtypes available in the CURRENTLY LOADED studies so the
  // picker only lists values that actually exist.
  const availableSubtypes = useMemo(() => {
    const set = new Set();
    studies.forEach(s => {
      const v = (s.phase || '').trim();
      set.add(v || '__none__');
    });
    const arr = Array.from(set);
    arr.sort((a, b) => {
      if (a === '__none__') return 1;
      if (b === '__none__') return -1;
      return a.localeCompare(b, lang === 'es' ? 'es' : 'en');
    });
    return arr;
  }, [studies, lang]);

  const effectiveSubtypes = filterSubtypes === null ? availableSubtypes : filterSubtypes;
  const allSubtypesSelected = filterSubtypes === null
    || (availableSubtypes.length > 0 && availableSubtypes.every(s => filterSubtypes.includes(s)));

  const toggleSubtype = (value) => {
    setFilterSubtypes(prev => {
      const current = prev === null ? availableSubtypes.slice() : prev.slice();
      const idx = current.indexOf(value);
      if (idx >= 0) current.splice(idx, 1);
      else current.push(value);
      return current;
    });
  };

  const selectAllSubtypes = () => setFilterSubtypes(null);
  const selectNoSubtypes = () => setFilterSubtypes([]);

  const studyById = useMemo(() => {
    const m = {};
    studies.forEach(s => { m[s.study_id] = s; });
    return m;
  }, [studies]);

  // Filter appointments by the selected subtypes (client-side).
  const visibleAppointments = useMemo(() => {
    let list = appointments;
    // URS-F306: las citas anuladas NUNCA aparecen en la agenda. Siguen
    // accesibles en la ficha del voluntario (con toggle "Mostrar anuladas").
    list = list.filter(a => a.status !== 'anulada');
    // URS-F286/URS-F396: filtro client-side por estudio (multiseleccion).
    //   filterStudies === null -> todos
    //   filterStudies === []   -> ninguno
    //   filterStudies === [id...] -> solo esos
    if (filterStudies !== null) {
      const allow = new Set(filterStudies);
      list = list.filter(a => a.study_id && allow.has(a.study_id));
    }
    if (filterStatus !== 'all') {
      list = list.filter(a => a.status === filterStatus);
    }
    if (allSubtypesSelected) return list;
    const allowed = new Set(effectiveSubtypes);
    return list.filter(a => {
      const s = studyById[a.study_id];
      const phase = (s?.phase || '').trim() || '__none__';
      // Keep Qsalud / no-study appointments visible under '__none__' bucket
      if (!a.study_id) return allowed.has('__none__');
      return allowed.has(phase);
    });
  }, [appointments, effectiveSubtypes, allSubtypesSelected, studyById, filterStatus, filterStudies]);

  return (
    <div className="space-y-4" data-testid="agenda-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarBlank size={28} weight="duotone" className="text-[#1f3e80]" />
          <h1 className="text-3xl font-heading font-semibold tracking-tight text-stone-900">{lb.title}</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="my-blocks-btn"
            onClick={() => setShowMyBlocksDialog(true)}
            variant="outline"
            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50 hover:border-indigo-300 rounded-sm gap-2"
            title={lang === 'es' ? 'Gestionar mis bloqueos personales de agenda' : 'Manage my personal schedule blocks'}
          >
            <UserCircle size={16} weight="duotone" />{lang === 'es' ? 'Bloqueo personal' : 'Personal block'}
          </Button>
          <Button
            data-testid="phone-calls-btn"
            onClick={() => setShowCalls(true)}
            variant="outline"
            className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-sm gap-2"
            title={lang === 'es' ? 'Citas QSALUD TFNO pendientes del día' : 'Pending QSALUD TFNO calls of the day'}
          >
            <Phone size={16} weight="duotone" />{lang === 'es' ? 'Llamadas del día' : 'Calls of the day'}
          </Button>
          <Button
            data-testid="block-slot-btn"
            onClick={() => openBlockDialog(fmtDate(currentDate), '09:00', '09:15')}
            variant="outline"
            className="border-red-200 text-red-700 hover:bg-red-50 hover:border-red-300 rounded-sm gap-2"
            title={lang === 'es' ? 'Bloquear un hueco para todos los estudios (incluido Qsalud)' : 'Block a slot for all studies (including Qsalud)'}
          >
            <Lock size={16} weight="duotone" />{lang === 'es' ? 'Bloquear' : 'Block'}
          </Button>
          <Button data-testid="new-appointment-btn" onClick={() => openNew()} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm gap-2">
            <Plus size={16} />{lb.new}
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" data-testid="nav-prev" onClick={() => navigate(-1)} className="h-8 w-8 rounded-sm"><CaretLeft size={16} /></Button>
          <Button variant="outline" size="sm" data-testid="nav-today" onClick={goToday} className="rounded-sm text-xs h-8">{lb.today}</Button>
          <Button variant="outline" size="icon" data-testid="nav-next" onClick={() => navigate(1)} className="h-8 w-8 rounded-sm"><CaretRight size={16} /></Button>
          <h2 className="text-base font-heading font-semibold text-stone-800 ml-2" data-testid="agenda-header-title">{headerTitle()}</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* URS-F396: filtro de estudios MULTI-SELECCION (mismo estilo que subtipos) */}
          {(() => {
            const activeStudies = [...studies]
              .filter(s => s.status === 'activo')
              .sort((a, b) => (a.code || '').localeCompare(b.code || '', undefined, { numeric: true, sensitivity: 'base' }));
            const counts = {};
            for (const a of appointments) {
              if (a.study_id) counts[a.study_id] = (counts[a.study_id] || 0) + 1;
            }
            const totalStudies = activeStudies.length;
            const effectiveStudies = filterStudies === null
              ? activeStudies.map(s => s.study_id)
              : filterStudies;
            const allStudiesSelected = filterStudies === null
              || (Array.isArray(filterStudies) && filterStudies.length === totalStudies && totalStudies > 0);
            const toggleStudy = (id) => {
              setFilterStudies(prev => {
                const base = prev === null ? activeStudies.map(s => s.study_id) : [...prev];
                const idx = base.indexOf(id);
                if (idx >= 0) base.splice(idx, 1);
                else base.push(id);
                return base;
              });
            };
            const selectAllStudies = () => setFilterStudies(null);
            const selectNoStudies = () => setFilterStudies([]);
            const triggerLabel = allStudiesSelected
              ? lb.all_studies
              : effectiveStudies.length === 0
                ? lb.select_none
                : effectiveStudies.length === 1
                  ? (activeStudies.find(s => s.study_id === effectiveStudies[0])?.code || effectiveStudies[0])
                  : lb.studies_count(effectiveStudies.length, totalStudies);
            return (
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    data-testid="filter-study-trigger"
                    className="h-8 text-xs bg-white border-stone-200 font-normal gap-2 w-48 justify-between"
                    title={triggerLabel}
                  >
                    <span className="font-medium text-stone-700 truncate">{triggerLabel}</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0" align="end">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-stone-100 bg-stone-50">
                    <span className="text-xs font-semibold text-stone-700">{lb.study}</span>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={selectAllStudies}
                        data-testid="filter-study-all"
                        className="text-[11px] text-[#1f3e80] hover:underline px-1"
                      >
                        {lb.select_all}
                      </button>
                      <span className="text-stone-300">·</span>
                      <button
                        type="button"
                        onClick={selectNoStudies}
                        data-testid="filter-study-none"
                        className="text-[11px] text-stone-500 hover:underline px-1"
                      >
                        {lb.select_none}
                      </button>
                    </div>
                  </div>
                  <div className="max-h-[320px] overflow-y-auto py-1">
                    {activeStudies.length === 0 ? (
                      <p className="text-xs text-stone-400 italic px-3 py-2">
                        {lang === 'es' ? 'No hay estudios activos' : 'No active studies'}
                      </p>
                    ) : activeStudies.map(s => {
                      const checked = effectiveStudies.includes(s.study_id);
                      return (
                        <label
                          key={s.study_id}
                          data-testid={`filter-study-${s.study_id}`}
                          className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-stone-50 cursor-pointer"
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => toggleStudy(s.study_id)}
                          />
                          <span
                            className="w-2 h-2 rounded-full shrink-0"
                            style={{ backgroundColor: s.color || '#6B7280' }}
                            aria-hidden
                          />
                          <span className="text-stone-700 flex-1 truncate">{s.code || s.study_id}</span>
                          {counts[s.study_id] > 0 && (
                            <span className="ml-auto bg-stone-100 text-stone-700 text-[10px] font-semibold px-1.5 py-0.5 rounded-sm shrink-0">
                              {counts[s.study_id]}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                </PopoverContent>
              </Popover>
            );
          })()}

          {/* URS-F247: status filter */}
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="filter-status" className="w-44 h-8 text-xs bg-white border-stone-200 gap-2">
              <span className="text-stone-500">{lb.status}:</span>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{lang === 'es' ? 'Todos' : 'All'}</SelectItem>
              {(apptStatuses && apptStatuses.length > 0
                ? apptStatuses.map(s => [s.key, s.color || '#5b6473', lang === 'es' ? s.label_es : s.label_en])
                : [
                    ['citada', '#60a5fa', lb.citada],
                    ['sala_espera', '#fbbf24', lb.sala_espera],
                    ['especialista', '#c084fc', lb.especialista],
                    ['sondas', '#2dd4bf', lb.sondas],
                    ['terminada', '#4ade80', lb.terminada],
                    ['no_asiste', '#f87171', lb.no_asiste],
                  ]
              )
                // URS-F306: 'anulada' no se muestra en la agenda, se excluye del filtro.
                .filter(([st]) => st !== 'anulada')
                .map(([st, color, label]) => (
                  <SelectItem key={st} value={st}>
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: color }} />
                      {label || lb[st] || st}
                    </span>
                  </SelectItem>
                ))}
            </SelectContent>
          </Select>

          {/* Subtype multi-select filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                data-testid="filter-subtype-trigger"
                className="h-8 text-xs bg-white border-stone-200 font-normal gap-2"
              >
                <span className="text-stone-500">{lb.subtype}:</span>
                <span className="font-medium text-stone-700">
                  {allSubtypesSelected
                    ? lb.all_subtypes
                    : effectiveSubtypes.length === 0
                      ? lb.select_none
                      : lb.subtypes_count(effectiveSubtypes.length, availableSubtypes.length)}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-0" align="end">
              <div className="flex items-center justify-between px-3 py-2 border-b border-stone-100 bg-stone-50">
                <span className="text-xs font-semibold text-stone-700">{lb.subtype}</span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={selectAllSubtypes}
                    data-testid="filter-subtype-all"
                    className="text-[11px] text-[#1f3e80] hover:underline px-1"
                  >
                    {lb.select_all}
                  </button>
                  <span className="text-stone-300">·</span>
                  <button
                    type="button"
                    onClick={selectNoSubtypes}
                    data-testid="filter-subtype-none"
                    className="text-[11px] text-stone-500 hover:underline px-1"
                  >
                    {lb.select_none}
                  </button>
                </div>
              </div>
              <div className="max-h-[280px] overflow-y-auto py-1">
                {availableSubtypes.length === 0 ? (
                  <p className="text-xs text-stone-400 italic px-3 py-2">
                    {lang === 'es' ? 'No hay subtipos' : 'No subtypes'}
                  </p>
                ) : availableSubtypes.map(value => {
                  const checked = effectiveSubtypes.includes(value);
                  const label = value === '__none__' ? lb.no_subtype : value;
                  return (
                    <label
                      key={value}
                      data-testid={`filter-subtype-${value}`}
                      className="flex items-center gap-2 px-3 py-1.5 text-sm hover:bg-stone-50 cursor-pointer"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={() => toggleSubtype(value)}
                      />
                      <span className={`text-stone-700 ${value === '__none__' ? 'italic text-stone-500' : ''}`}>{label}</span>
                    </label>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>

          <div className="flex bg-stone-100 rounded-sm p-0.5">
            {[VIEWS.day, VIEWS.week, VIEWS.month, VIEWS.schedule].map(v => (
              <Button key={v} variant={view === v ? 'default' : 'ghost'} size="sm" data-testid={`view-${v}`}
                onClick={() => setView(v)}
                className={`text-xs h-7 rounded-sm ${view === v ? 'bg-[#1f3e80] text-white hover:bg-[#2a4f8a]' : 'text-stone-600'}`}>
                {lb[v]}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {(() => {
        const visibleStudyIds = new Set(visibleAppointments.map(a => a.study_id).filter(Boolean));
        const legendStudies = studies.filter(s => s.status === 'activo' && visibleStudyIds.has(s.study_id) && (filterStudies === null || filterStudies.includes(s.study_id)));
        return legendStudies.length > 0 && (
          <div className="flex flex-wrap gap-3">
            {legendStudies.map(s => (
              <StudyLegendChip key={s.study_id} study={s} lang={lang} />
            ))}
          </div>
        );
      })()}

      {view === VIEWS.day && <DayView date={currentDate} appointments={visibleAppointments} studies={studies} onClickAppt={openEdit} onClickSlot={openNew} onClickProgrammedSlot={openNewFromSlot} lang={lang} onDragStart={handleDragStart} onDrop={handleDrop} scheduleSlots={scheduleSlots} slotLocks={slotLocks} scheduleBlocks={scheduleBlocks} onContextMenuSlot={handleContextMenuSlot} onClickBlock={handleClickBlock} />}
      {view === VIEWS.week && <WeekView date={currentDate} appointments={visibleAppointments} studies={studies} onClickAppt={openEdit} onClickSlot={openNew} onClickProgrammedSlot={openNewFromSlot} lang={lang} onDragStart={handleDragStart} onDrop={handleDrop} scheduleSlots={scheduleSlots} slotLocks={slotLocks} scheduleBlocks={scheduleBlocks} onContextMenuSlot={handleContextMenuSlot} onClickBlock={handleClickBlock} />}
      {view === VIEWS.month && <MonthView date={currentDate} appointments={visibleAppointments} studies={studies} onClickAppt={openEdit} onClickDay={(d) => { setCurrentDate(d); setView(VIEWS.day); }} lang={lang} />}
      {view === VIEWS.schedule && <ScheduleYearView lang={lang} />}

      <Dialog open={showForm} onOpenChange={(v) => { if (!v) closeForm(); else setShowForm(true); }}>
        <DialogContent data-testid="appointment-form-dialog" className="max-w-6xl p-0 overflow-hidden">
          <div data-testid="appointment-form-dialog-body" className="contents">
          <div className="flex flex-col sm:flex-row sm:h-[620px]">
            {/* LEFT: Form */}
            <div className="flex-1 flex flex-col overflow-y-auto min-w-0">
              <DialogHeader className="px-6 pt-6 pb-2">
                <div className="flex items-center justify-between gap-3">
                  <DialogTitle>{editing ? lb.edit : lb.new}</DialogTitle>
                  {editing && editing.appointment_id && isAdmin && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid="appt-audit-btn"
                      onClick={() => setShowApptAudit(true)}
                      className="rounded-sm gap-1 h-7 text-xs text-[#1f3e80] border-[#1f3e80]/40 hover:bg-[#1f3e80]/5 mr-6"
                      title={lang === 'es' ? 'Ver historial de auditoria de esta cita' : 'View this appointment audit timeline'}
                    >
                      <ClockCounterClockwise size={14} />{lang === 'es' ? 'Auditoría' : 'Audit'}
                    </Button>
                  )}
                </div>
              </DialogHeader>
              <div className="flex-1 px-6 pb-2 space-y-4 overflow-y-auto">
            <div className="space-y-1.5">
              <Label>{lb.study}{lockedFromSlot && <span className="ml-1 text-[10px] text-stone-400">({lang === 'es' ? 'desde programacion' : 'from schedule'})</span>}</Label>
              <Select value={isQsalud ? (form.visit_code === 'QSALUD TFNO' ? '_qsalud_tfno' : '_qsalud') : (form.study_id || '_none')} onValueChange={v => handleStudyChange(v === '_none' ? '' : v)} disabled={!!editing || lockedFromSlot}>
                <SelectTrigger data-testid="appt-study-select"><SelectValue placeholder={lb.select_study} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">{lb.select_study}</SelectItem>
                  <SelectItem value="_qsalud">
                    <span className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-emerald-500" />
                      Qsalud — {lang === 'es' ? 'Sin estudio' : 'No study'}
                    </span>
                  </SelectItem>
                  <SelectItem value="_qsalud_tfno" data-testid="appt-study-qsalud-tfno">
                    <span className="flex items-center gap-2">
                      <Phone size={12} className="text-emerald-600" />
                      QSALUD TFNO — {lang === 'es' ? 'Sin estudio (telefónica)' : 'No study (phone)'}
                    </span>
                  </SelectItem>
                  {activeStudiesWithVisits.map(s => (
                    <SelectItem key={s.study_id} value={s.study_id}>
                      <span className="flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color || '#6B7280' }} />
                        {s.code} — {s.title}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {isQsalud && (
              <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-sm">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-xs font-medium text-emerald-700">{form.visit_code === 'QSALUD TFNO' ? (lang === 'es' ? 'Cita QSALUD TFNO — Revisión telefónica del Qsalud, sin estudio asociado (sin recordatorios)' : 'QSALUD TFNO appointment — Phone Qsalud review, no study (no reminders)') : (lang === 'es' ? 'Cita Qsalud — Visita general sin estudio asociado' : 'Qsalud appointment — General visit without study')}</span>
              </div>
            )}
            {/* 2) Visit (skipped for Qsalud) */}
            {isQsalud ? (
              <div className="space-y-1.5">
                <Label>{lb.visit}</Label>
                <Input data-testid="appt-visit-display" value={form.visit_code === 'QSALUD TFNO' ? 'QSALUD TFNO' : 'Qsalud'} readOnly className="bg-stone-50 text-stone-600 cursor-not-allowed" />
              </div>
            ) : (
              <div className="space-y-1.5">
                <Label>{lb.visit} <span className="text-red-500">*</span>{lockedFromSlot && <span className="ml-1 text-[10px] text-stone-400">({lang === 'es' ? 'desde programacion' : 'from schedule'})</span>}</Label>
                <Select value={form.visit_code || '_none'} onValueChange={v => setForm(p => ({ ...p, visit_code: v === '_none' ? '' : v, volunteer_id: '', volunteer_name: '' }))} disabled={lockedFromSlot}>
                  <SelectTrigger data-testid="appt-visit-select"><SelectValue placeholder={lb.select_visit} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">{lb.select_visit}</SelectItem>
                    {studyVisits.map(v => (
                      <SelectItem key={v.visit_id} value={v.code}>{v.code} — {v.name || `${lang === 'es' ? 'Dia' : 'Day'} ${v.day}`}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {/* 3) Volunteer with inline search, depends on study + visit */}
            <div className="space-y-1.5">
              <Label>{lb.volunteer} <span className="text-red-500">*</span></Label>
              <VolunteerComboSearch
                dataTestid="appt-volunteer-combo"
                disabled={!!editing || (!isQsalud && (!form.study_id || !form.visit_code))}
                options={volunteersForAppointment}
                value={form.volunteer_id || ''}
                onChange={(id, opt) => {
                  if (!id) {
                    setForm(p => ({ ...p, volunteer_id: '', volunteer_name: '' }));
                    return;
                  }
                  const name = opt?.volunteer_name || (() => {
                    const pd = opt?.personal_data || {};
                    return `${pd.nombre || opt?.name || ''} ${pd.apellidos || opt?.surname || ''}`.trim();
                  })();
                  setForm(p => ({ ...p, volunteer_id: id, volunteer_name: name }));
                }}
                getLabel={formatVolForCombo}
                placeholder={!isQsalud && (!form.study_id || !form.visit_code)
                  ? (lang === 'es' ? 'Selecciona estudio y visita primero...' : 'Select study and visit first...')
                  : (lang === 'es' ? 'Buscar voluntario por nombre, V-code o DNI...' : 'Search by name, V-code or DNI...')}
                emptyHint={volunteerEmptyHint}
              />
              {volunteerListEmpty && (
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  data-testid="assign-volunteer-shortcut"
                  onClick={() => { closeForm(); routerNavigate(`/studies/${form.study_id}?tab=volunteers`); }}
                  className="w-full mt-1.5 rounded-sm gap-1 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
                >
                  <UserPlus size={12} />
                  {lang === 'es' ? 'Asignar voluntario al estudio' : 'Assign volunteer to study'}
                </Button>
              )}
              {form.volunteer_id && form.volunteer_id !== '_none' && (
                <Button
                  variant="outline"
                  size="sm"
                  data-testid="view-volunteer-btn"
                  onClick={() => setShowVolunteerFull(true)}
                  className="w-full mt-1.5 rounded-sm gap-1 text-xs text-[#1f3e80] border-[#1f3e80]/30 hover:border-[#1f3e80]/50"
                >
                  <User size={12} />{lang === 'es' ? 'Ver voluntario' : 'View volunteer'}
                </Button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>{lb.date} <span className="text-red-500">*</span></Label>
                <Input data-testid="appt-date-input" type="date" value={form.date} onChange={e => setForm(p => ({ ...p, date: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>{lb.time}</Label>
                <Input data-testid="appt-time-input" type="time" step="900" min="08:00" max="21:00" value={form.time} onChange={e => setForm(p => ({ ...p, time: e.target.value }))} />
              </div>
            </div>
            <HolidayWarning date={form.date} lang={lang} />
            {editing && editing.duration_minutes && (
              <div className="flex items-center gap-2 px-3 py-2 bg-stone-50 border border-stone-200 rounded-sm">
                <span className="text-xs text-stone-500">{lang === 'es' ? 'Duracion' : 'Duration'}:</span>
                <span className="text-sm font-semibold text-stone-800" data-testid="appt-duration-display">{editing.duration_minutes} min</span>
              </div>
            )}
            <div className="space-y-1.5">
              <Label>{lb.status}</Label>
              <Select value={form.status} onValueChange={v => setForm(p => ({ ...p, status: v }))}>
                <SelectTrigger data-testid="appt-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(apptStatuses && apptStatuses.length > 0
                    ? apptStatuses
                    : ['citada', 'anulada', 'no_asiste', 'sala_espera', 'especialista', 'sondas', 'terminada'].map(k => ({ key: k, label_es: lb[k], label_en: lb[k], color: '#5b6473' }))
                  ).map(s => (
                    <SelectItem key={s.key} value={s.key}>
                      <span className="inline-flex items-center gap-2">
                        <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                        {lang === 'es' ? s.label_es : s.label_en}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{lb.notes}</Label>
              <Textarea data-testid="appt-notes-input" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} className="min-h-[50px]" />
            </div>
          </div>
              {editing && editing.appointment_id && (
                <div className="px-6 pb-2" data-testid="appt-id-display">
                  <p className="text-[11px] text-stone-400 select-all">
                    ID cita: <span className="font-mono text-stone-500">{editing.appointment_id}</span>
                  </p>
                </div>
              )}
              <div className="px-6 py-3 border-t border-stone-200 flex justify-between">
                <div className="flex gap-2">
                  {/* URS-F246: el boton "Revisar/Ir a Qsalud" tambien se muestra
                      cuando la propia cita es de tipo Qsalud, para que el
                      operador pueda saltar directo al Qsalud del voluntario
                      sin tener que cerrar la cita y buscarlo manualmente. */}
                  {editing && form.volunteer_id && (isQsalud || (form.study_id && isValVisit)) && (
                    <Button
                      variant="outline"
                      data-testid="review-qsalud-btn"
                      onClick={() => setShowQsaludReview(true)}
                      className={`rounded-sm gap-1 leading-tight text-center ${(isQsalud || qsaludRecent) ? 'text-emerald-700 border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50' : 'text-amber-700 border-amber-300 hover:border-amber-500 hover:bg-amber-50'}`}
                      title={isQsalud
                        ? (lang === 'es' ? 'Abrir Qsalud del voluntario' : 'Open volunteer Qsalud')
                        : (qsaludRecent
                          ? (lang === 'es' ? `Qsalud revisado hace menos de 24h (${new Date(qsaludReviewedAt).toLocaleString(lang === 'es' ? 'es-ES' : 'en-US')})` : 'Qsalud reviewed less than 24h ago')
                          : (lang === 'es' ? 'Qsalud no revisado en las últimas 24h' : 'Qsalud not reviewed in the last 24h'))}
                    >
                      <Heartbeat size={14} /><span className="flex flex-col text-xs"><span>{isQsalud ? 'Ir a' : 'Revisar'}</span><span>Qsalud</span></span>
                    </Button>
                  )}
                  {editing && form.study_id && form.volunteer_id && !isQsalud && (
                    <Button
                      variant="outline"
                      data-testid="fill-questionnaire-btn"
                      onClick={goToFillQuestionnaire}
                      disabled={!qsaludGateOpen}
                      className="text-[#1f3e80] hover:text-[#2a4f8a] border-[#1f3e80]/30 hover:border-[#1f3e80]/50 rounded-sm gap-1 leading-tight text-center disabled:opacity-50"
                      title={!qsaludGateOpen ? (lang === 'es' ? 'Revisa primero el Qsalud del voluntario' : 'Review the volunteer Qsalud first') : ''}
                    >
                      <ClipboardText size={14} /><span className="flex flex-col text-xs"><span>Rellenar</span><span>Cuestionario</span></span>
                    </Button>
                  )}
                  {editing && isAdmin && (
                    <Button variant="outline" data-testid="delete-appt-btn" onClick={() => setShowDeleteConfirm(true)} className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300 rounded-sm gap-1">
                      <Trash size={14} />{lb.delete}
                    </Button>
                  )}
                  {editing && editing.volunteer_id && (
                    // URS-F390: imprimir Justificante de asistencia desde la
                    // edicion de una cita en la Agenda.
                    <Button
                      variant="outline"
                      data-testid="print-justificante-btn"
                      onClick={async () => {
                        try {
                          const resp = await axios.get(
                            `${API_URL}/api/appointments/${editing.appointment_id}/attendance-certificate.pdf`,
                            { withCredentials: true, responseType: 'blob' }
                          );
                          // URS-F390 fix: abrir dialogo de impresion en lugar
                          // de descargar (iframe oculto + print()).
                          const url = URL.createObjectURL(new Blob([resp.data], { type: 'application/pdf' }));
                          const iframe = document.createElement('iframe');
                          iframe.style.position = 'fixed';
                          iframe.style.right = '0';
                          iframe.style.bottom = '0';
                          iframe.style.width = '0';
                          iframe.style.height = '0';
                          iframe.style.border = '0';
                          iframe.src = url;
                          iframe.onload = () => {
                            try {
                              iframe.contentWindow.focus();
                              iframe.contentWindow.print();
                            } catch (err) {
                              window.open(url, '_blank');
                            }
                          };
                          document.body.appendChild(iframe);
                          setTimeout(() => {
                            try { URL.revokeObjectURL(url); iframe.remove(); } catch (_) { /* noop */ }
                          }, 60000);
                        } catch (e) {
                          toast.error(lang === 'es' ? 'Error al generar el justificante' : 'Error generating certificate');
                        }
                      }}
                      className="rounded-sm gap-1 border-[#1f3e80]/40 text-[#1f3e80] hover:bg-[#1f3e80]/5"
                    >
                      <ClipboardText size={14} />{lang === 'es' ? 'Imprimir Justificante' : 'Print Certificate'}
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button variant="ghost" onClick={closeForm}>{lb.cancel}</Button>
                  <Button data-testid="save-and-print-appt-btn" onClick={handleSaveAndPrint} disabled={saving} variant="outline" className="rounded-sm border-[#1f3e80]/40 text-[#1f3e80] hover:bg-[#1f3e80]/5">
                    {saving ? '...' : (lang === 'es' ? 'Guardar e imprimir' : 'Save and print')}
                  </Button>
                  <Button data-testid="save-appt-btn" onClick={handleSave} disabled={saving} className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white rounded-sm">
                    {saving ? '...' : lb.save}
                  </Button>
                </div>
              </div>
            </div>
            {/* RIGHT: Mini agenda - widened (URS-F224) so the operator
                  can read full volunteer/visit info while editing. */}
            <div className="hidden sm:flex flex-col w-[420px] shrink-0 border-l border-stone-200 bg-white">
              <MiniDayView
                date={form.date}
                appointments={appointments}
                studies={studies}
                scheduleSlots={scheduleSlots}
                scheduleBlocks={scheduleBlocks}
                activeStudyId={form.study_id || null}
                selectedTime={form.time}
                lang={lang}
                onSelectTime={(d, t) => setForm(p => ({ ...p, date: d, time: t }))}
              />
            </div>
          </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Qsalud review modal — iframe to /volunteers/{id}?embedded=1 */}
      <Dialog open={showQsaludReview} onOpenChange={setShowQsaludReview}>
        <DialogContent className="max-w-5xl w-[95vw] h-[90vh] p-0 flex flex-col" data-testid="qsalud-review-dialog">
          <DialogHeader className="px-4 pt-3 pb-2 border-b border-stone-200">
            <DialogTitle className="flex items-center gap-2 text-base">
              <Heartbeat size={18} className="text-emerald-600" />
              {lang === 'es' ? 'Revisar Qsalud' : 'Review Qsalud'}
              <span className="text-xs font-normal text-stone-500 ml-2">
                {form.volunteer_name} · {form.volunteer_id}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-stone-50">
            {form.volunteer_id && (
              <iframe
                key={form.volunteer_id}
                src={`/volunteers/${form.volunteer_id}?embedded=1&tab=health`}
                title="Qsalud"
                className="w-full h-full border-0"
                data-testid="qsalud-iframe"
              />
            )}
          </div>
          <div className="px-4 py-2 border-t border-stone-200 flex justify-between items-center bg-white">
            <p className="text-xs text-stone-500">
              {lang === 'es'
                ? 'Pulsa "Guardar" dentro del Qsalud para que se considere revisado.'
                : 'Click "Save" inside the Qsalud to mark it as reviewed.'}
            </p>
            <Button variant="ghost" onClick={() => setShowQsaludReview(false)}>{lb.cancel}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* URS-F249: ficha completa del voluntario en overlay (mismo patron iframe que Qsalud) */}
      <Dialog open={showVolunteerFull} onOpenChange={setShowVolunteerFull}>
        <DialogContent className="max-w-6xl w-[95vw] h-[92vh] p-0 flex flex-col" data-testid="volunteer-full-dialog">
          <DialogHeader className="px-4 pt-3 pb-2 border-b border-stone-200">
            <DialogTitle className="flex items-center gap-2 text-base">
              <User size={18} className="text-[#1f3e80]" />
              {lang === 'es' ? 'Ficha del voluntario' : 'Volunteer record'}
              <span className="text-xs font-normal text-stone-500 ml-2">
                {form.volunteer_name} · {form.volunteer_id}
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-hidden bg-stone-50">
            {form.volunteer_id && (
              <iframe
                key={`vol-full-${form.volunteer_id}`}
                src={`/volunteers/${form.volunteer_id}?embedded=1`}
                title="Volunteer"
                className="w-full h-full border-0"
                data-testid="volunteer-full-iframe"
              />
            )}
          </div>
          <div className="px-4 py-2 border-t border-stone-200 flex justify-between items-center bg-white">
            <p className="text-xs text-stone-500">
              {lang === 'es'
                ? 'Puedes navegar entre pestanas, enviar documentos a la tableta y editar el Qsalud sin perder esta cita.'
                : 'You can navigate tabs, send documents to the tablet, and edit Qsalud without losing this appointment.'}
            </p>
            <Button variant="ghost" onClick={() => setShowVolunteerFull(false)}>{lb.cancel}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* URS-F416e: Modal "Mis bloqueos personales" (acceso rapido desde el header de Agenda) */}
      <Dialog open={showMyBlocksDialog} onOpenChange={setShowMyBlocksDialog}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col p-0" data-testid="my-blocks-dialog">
          <DialogHeader className="px-6 pt-6 pb-2">
            <DialogTitle className="text-[#1f3e80]">
              {lang === 'es' ? 'Mis bloqueos personales' : 'My personal blocks'}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-stone-500 px-6 pb-2">
            {lang === 'es'
              ? 'Marca las franjas en que NO estarás disponible. Si eres el único IP/CoIP del estudio en ese hueco, la cita quedará bloqueada; si hay otros IP disponibles, Reclutamiento solo verá un aviso.'
              : 'Mark the slots when you will NOT be available. If you are the only IP/CoIP of the study in that slot, appointments will be blocked; if others are available, Recruitment will only see a warning.'}
          </p>
          <div className="flex-1 overflow-y-auto px-6">
            {user?.id ? (
              <UserScheduleBlocksSection userId={user.id} lang={lang} hideCard initialDate={fmtDate(currentDate)} />
            ) : null}
          </div>
          <DialogFooter className="px-6 py-4 border-t border-stone-200">
            <Button variant="outline" onClick={() => setShowMyBlocksDialog(false)} data-testid="my-blocks-close-btn">
              {lang === 'es' ? 'Cerrar' : 'Close'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* URS-F416b: Modal de cobertura — partial (aviso con confirmacion) o none (bloqueo duro) */}
      <Dialog open={!!coverageWarning} onOpenChange={v => { if (!v) { setCoverageWarning(null); setCoverageAck(false); setCoverageMove(null); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="coverage-warning-dialog">
          <DialogHeader>
            <DialogTitle className={coverageWarning?.coverage === 'none' ? 'text-red-700' : 'text-amber-700'}>
              {coverageWarning?.coverage === 'none'
                ? ((coverageMove || editing) ? (lang === 'es' ? 'No se puede mover la cita: sin cobertura' : 'Cannot move appointment: no coverage') : (lang === 'es' ? 'No se puede crear la cita: sin cobertura' : 'Cannot create appointment: no coverage'))
                : (lang === 'es' ? 'Aviso: cobertura parcial del staff' : 'Warning: partial staff coverage')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-stone-700">
              {coverageWarning?.coverage === 'none'
                ? (lang === 'es'
                  ? 'Ningun IP/CoIP del staff esta disponible en la fecha y hora seleccionadas:'
                  : 'No IP/CoIP of the study staff is available at the selected date/time:')
                : (lang === 'es'
                  ? 'En la fecha y hora seleccionadas hay miembros del staff del estudio con bloqueo personal:'
                  : 'On the selected date/time, some study staff members have a personal block:')}
            </p>
            <div className={`space-y-1.5 border rounded-sm p-3 ${coverageWarning?.coverage === 'none' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
              {(coverageWarning?.blocked || []).map(b => (
                <div key={b.block_id} className="flex items-start gap-2 text-xs" data-testid={`cov-blocked-${b.user_id}`}>
                  <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1.5 ${coverageWarning?.coverage === 'none' ? 'bg-red-500' : 'bg-amber-500'}`} />
                  <div className="flex-1">
                    <span className="font-semibold text-stone-800">{b.name}</span>
                    <span className="text-stone-500">
                      {' — '}
                      {b.all_day
                        ? (lang === 'es' ? 'todo el día' : 'all day')
                        : `${b.start_time}–${b.end_time}`}
                    </span>
                    <div className="text-stone-600 italic">{b.reason}</div>
                  </div>
                </div>
              ))}
            </div>
            {coverageWarning?.coverage === 'partial' && (
              <>
                <p className="text-stone-700">
                  {lang === 'es'
                    ? `Atenderán las citas: ${(coverageWarning?.available || []).map(a => a.name).join(', ') || '—'}.`
                    : `Available: ${(coverageWarning?.available || []).map(a => a.name).join(', ') || '—'}.`}
                </p>
                <label className="flex items-center gap-2 text-xs text-stone-700 bg-stone-50 border border-stone-200 rounded-sm p-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={coverageAck}
                    onChange={e => setCoverageAck(e.target.checked)}
                    data-testid="coverage-ack-checkbox"
                  />
                  <span>
                    {lang === 'es'
                      ? `He leído el aviso y confirmo ${(coverageMove || editing) ? 'el cambio' : 'la creación'} de la cita.`
                      : 'I have read this notice and confirm the appointment.'}
                  </span>
                </label>
              </>
            )}
            {coverageWarning?.coverage === 'none' && (
              <p className="text-xs text-stone-600 bg-stone-50 border border-stone-200 rounded-sm p-2">
                {lang === 'es'
                  ? 'Elige otra fecha/hora, o retira el bloqueo del profesional desde Configuración > Usuarios.'
                  : 'Choose another date/time, or remove the block from Settings > Users.'}
              </p>
            )}
          </div>
          <DialogFooter>
            {coverageWarning?.coverage === 'none' ? (
              <Button
                onClick={() => { setCoverageWarning(null); setCoverageAck(false); setCoverageMove(null); }}
                data-testid="coverage-close-btn"
              >
                {lang === 'es' ? 'Entendido' : 'Got it'}
              </Button>
            ) : (
              <>
                <Button variant="ghost" onClick={() => { setCoverageWarning(null); setCoverageAck(false); setCoverageMove(null); }} data-testid="coverage-cancel-btn">
                  {lang === 'es' ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button
                  disabled={!coverageAck || saving}
                  onClick={() => {
                    const mv = coverageMove;
                    setCoverageWarning(null); setCoverageAck(false); setCoverageMove(null);
                    if (mv) executeDrop(mv.appt, mv.newDate, mv.newTime); else doSave({ skipCoverage: true });
                  }}
                  data-testid="coverage-confirm-btn"
                >
                  {(coverageMove || editing) ? (lang === 'es' ? 'Continuar y guardar cambio' : 'Continue and save') : (lang === 'es' ? 'Continuar y crear cita' : 'Continue and create')}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showOutOfSchedule} onOpenChange={setShowOutOfSchedule}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-amber-700">
              {lang === 'es' ? 'Fuera de programacion' : 'Out of schedule'}
            </DialogTitle>
          </DialogHeader>          <p className="text-sm text-stone-600">
            {lang === 'es'
              ? `La fecha y hora seleccionadas no estan dentro de la programacion del estudio. ¿Deseas ${(pendingDrop || editing) ? 'mover' : 'crear'} la cita de todas formas?`
              : 'The selected date and time are not within the study schedule. Do you want to create the appointment anyway?'}
          </p>
          <DialogFooter className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => { setShowOutOfSchedule(false); setPendingDrop(null); }}>
              {lb.cancel}
            </Button>
            <Button onClick={async () => { if (pendingDrop) { const pd = pendingDrop; setPendingDrop(null); setShowOutOfSchedule(false); await moveWithCoverage(pd.appt, pd.newDate, pd.newTime); } else { setShowOutOfSchedule(false); await doSave(); } }} className="bg-amber-600 hover:bg-amber-700 text-white rounded-sm">
              {(pendingDrop || editing) ? (lang === 'es' ? 'Mover igualmente' : 'Move anyway') : (lang === 'es' ? 'Crear igualmente' : 'Create anyway')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <VolunteerModal volunteerId={viewVolunteerId} open={!!viewVolunteerId} onClose={() => setViewVolunteerId(null)} />

      <AppointmentAuditTimeline
        appointmentId={editing?.appointment_id}
        open={showApptAudit}
        onClose={() => setShowApptAudit(false)}
        lang={lang}
      />

      <ConfirmDeleteDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title={lang === 'es' ? 'Eliminar cita' : 'Delete appointment'}
        description={lang === 'es' ? '¿Estás seguro de que deseas eliminar esta cita? Esta acción no se puede deshacer.' : 'Are you sure you want to delete this appointment? This action cannot be undone.'}
        onConfirm={handleDelete}
        loading={deletingAppt}
      />

      <PhoneCallsSheet open={showCalls} onOpenChange={setShowCalls} date={fmtDate(currentDate)} lang={lang} onChanged={loadData} />
      {/* URS-F252: Bloquear hueco global */}
      <ScheduleBlockDialog
        open={showBlockDialog}
        onClose={() => setShowBlockDialog(false)}
        defaultDate={blockDefaults.date}
        defaultStartTime={blockDefaults.startTime}
        defaultEndTime={blockDefaults.endTime}
        lang={lang}
        onCreated={reloadBlocks}
      />

      <AgendaContextMenu
        open={ctxMenu.open}
        x={ctxMenu.x}
        y={ctxMenu.y}
        date={ctxMenu.date}
        time={ctxMenu.time}
        lang={lang}
        onClose={() => setCtxMenu(c => ({ ...c, open: false }))}
        onNewAppt={(d, t) => openNew(d, t)}
        onBlock={(d, t) => openBlockDialog(d, t, addMinutes(t, 15))}
      />

      <ConfirmDeleteDialog
        open={!!blockToDelete}
        onOpenChange={(v) => { if (!v) setBlockToDelete(null); }}
        title={lang === 'es' ? 'Eliminar bloqueo' : 'Delete block'}
        description={blockToDelete
          ? (lang === 'es'
            ? `¿Eliminar el bloqueo del ${blockToDelete.date} ${blockToDelete.start_time}-${blockToDelete.end_time} (${blockToDelete.reason || 'sin motivo'})?`
            : `Delete block on ${blockToDelete.date} ${blockToDelete.start_time}-${blockToDelete.end_time} (${blockToDelete.reason || 'no reason'})?`)
          : ''}
        onConfirm={confirmDeleteBlock}
      />
    </div>
  );
}

